import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, IconButton, Button, TextField, InputAdornment,
    CircularProgress, Alert, List, ListItem, ListItemAvatar, ListItemText,
    Avatar, Chip, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import api from "../../../services/auth";
import segmentService from "../../../services/segmentService";

const TEAL = "#007B8A";

const profileLabel = (profile) => {
    const p = profile?.properties || {};
    const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
    return name || p.email || profile?.itemId || "Contact sans nom";
};

const profileSubtitle = (profile) => {
    const p = profile?.properties || {};
    return p.email || p.phoneNumber || p.phone || profile?.itemId || "";
};

const avatarLetter = (text) => (text ? text.charAt(0).toUpperCase() : "?");

/**
 * Shared dialog to manually add a contact to a segment.
 *
 * mode="pickSegment": a contact is fixed (profileId); user searches and picks a segment.
 * mode="pickContact": a segment is fixed (segmentId); user searches and picks a contact.
 */
const AddSegmentMemberDialog = ({
    open,
    onClose,
    mode = "pickSegment",
    profileId,
    profileName,
    segmentId,
    segmentName,
    onSuccess,
}) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [adding, setAdding] = useState({});
    const [added, setAdded] = useState({});
    const debounceRef = useRef(null);

    const isPickSegment = mode === "pickSegment";

    const runSearch = useCallback(async (term) => {
        setLoading(true);
        setError("");
        try {
            if (isPickSegment) {
                const res = await segmentService.getSegmentsPaginated({
                    page: 1,
                    pageSize: 20,
                    search: term || null,
                    sortField: "created",
                    sortDirection: "desc",
                });
                setResults(res.segments || []);
            } else {
                const res = await api.post("/profiles/search", {
                    offset: 0,
                    limit: 20,
                    search_term: term?.trim() || null,
                    sort_by: "properties.firstVisit:desc",
                });
                setResults(res.data?.profiles || []);
            }
        } catch (e) {
            console.error("Search failed:", e);
            setError(
                isPickSegment
                    ? "Impossible de charger les segments."
                    : "Impossible de charger les contacts."
            );
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [isPickSegment]);

    useEffect(() => {
        if (!open) return;
        setQuery("");
        setResults([]);
        setError("");
        setAdding({});
        setAdded({});
        runSearch("");
    }, [open, runSearch]);

    useEffect(() => {
        if (!open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(query), 350);
        return () => clearTimeout(debounceRef.current);
    }, [query, open, runSearch]);

    const handleAdd = async (targetSegmentId, targetProfileId, key) => {
        setAdding((prev) => ({ ...prev, [key]: true }));
        setError("");
        try {
            const result = await segmentService.addSegmentMember(targetSegmentId, targetProfileId);
            if (result.success) {
                setAdded((prev) => ({ ...prev, [key]: true }));
                if (onSuccess) onSuccess(result);
            } else {
                setError(result.message || "Échec de l'ajout.");
            }
        } catch (e) {
            setError(e.message || "Échec de l'ajout.");
        } finally {
            setAdding((prev) => ({ ...prev, [key]: false }));
        }
    };

    const title = isPickSegment
        ? "Ajouter le contact à un segment"
        : "Ajouter un contact au segment";

    const contextLabel = isPickSegment
        ? profileName || profileId
        : segmentName || segmentId;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isPickSegment ? <GroupIcon sx={{ color: TEAL }} /> : <PersonIcon sx={{ color: TEAL }} />}
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {contextLabel && (
                    <Typography variant="body2" sx={{ mb: 2, color: "#6B7280" }}>
                        {isPickSegment ? "Contact : " : "Segment : "}
                        <strong>{contextLabel}</strong>
                    </Typography>
                )}

                <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder={isPickSegment
                        ? "Rechercher un segment par nom..."
                        : "Rechercher un contact par nom, email ou téléphone..."}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#9CA3AF" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} sx={{ color: TEAL }} />
                    </Box>
                ) : results.length === 0 ? (
                    <Typography variant="body2" sx={{ textAlign: "center", py: 4, color: "#9CA3AF", fontStyle: "italic" }}>
                        Aucun résultat.
                    </Typography>
                ) : (
                    <List dense sx={{ maxHeight: 340, overflow: "auto" }}>
                        {results.map((item, idx) => {
                            const targetSegmentId = isPickSegment ? item.id : segmentId;
                            const targetProfileId = isPickSegment ? profileId : item.itemId;
                            const key = isPickSegment ? item.id : item.itemId;
                            const primary = isPickSegment
                                ? (item.name || item.metadata?.name || item.id)
                                : profileLabel(item);
                            const secondary = isPickSegment
                                ? (item.metadata?.description || item.description || "")
                                : profileSubtitle(item);
                            const isAdded = added[key];
                            const isAdding = adding[key];
                            return (
                                <React.Fragment key={key || idx}>
                                    <ListItem
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            py: 1,
                                        }}
                                    >
                                        <ListItemAvatar sx={{ minWidth: 40, flexShrink: 0 }}>
                                            <Avatar sx={{ bgcolor: isPickSegment ? "#E0F2F1" : "#EEF2FF", color: TEAL }}>
                                                {isPickSegment ? <GroupIcon fontSize="small" /> : avatarLetter(primary)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={primary}
                                            secondary={secondary}
                                            sx={{ flex: 1, minWidth: 0, mr: 1 }}
                                            primaryTypographyProps={{
                                                noWrap: true,
                                                sx: { fontWeight: 500 },
                                            }}
                                            secondaryTypographyProps={{ noWrap: true }}
                                        />
                                        <Box sx={{ flexShrink: 0, ml: "auto" }}>
                                            {isAdded ? (
                                                <Chip
                                                    icon={<CheckCircleIcon />}
                                                    label="Ajouté"
                                                    size="small"
                                                    sx={{ bgcolor: "#DCFCE7", color: "#166534" }}
                                                />
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={isAdding || !targetSegmentId || !targetProfileId}
                                                    startIcon={isAdding
                                                        ? <CircularProgress size={14} />
                                                        : <PersonAddIcon />}
                                                    onClick={() => handleAdd(targetSegmentId, targetProfileId, key)}
                                                    sx={{
                                                        textTransform: "none",
                                                        borderColor: TEAL,
                                                        color: TEAL,
                                                        whiteSpace: "nowrap",
                                                        "&:hover": { borderColor: TEAL, bgcolor: "#E0F2F1" },
                                                    }}
                                                >
                                                    Ajouter
                                                </Button>
                                            )}
                                        </Box>
                                    </ListItem>
                                    {idx < results.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddSegmentMemberDialog;
